import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact } from 'lodash-es';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '@env';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { CUSTOM_ERROR_MESSAGE, IS_EXTENDED_ERRORS } from '@shared/interceptors';
import {
  AddApproverRequest,
  ApprovalTransactionDetails,
  ApprovalTransactionListResponse,
  ApprovalTransactionSearchParams,
  CreatedUpdatedBy,
  UpdateApprovalStatusRequest,
} from '@shared/models';
import { BackendService } from '@shared/services/backend.service';
import { AuditActions } from '@shared/store/audit-store/audit.actions';

@Injectable({
  providedIn: 'root',
})
export class ApprovalRequestService {
  private readonly basePath = `${environment.transactionFlowEndpoint}/move-money/approval-requests`;

  constructor(
    private apiService: BackendService,
    private store: Store,
  ) {}

  /**
   * Search for approval requests with optional filters
   * GET /api/portal/v1/move-money/approval-requests
   */
  searchApprovalRequests(searchParams?: ApprovalTransactionSearchParams): Observable<ApprovalTransactionListResponse> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    if (searchParams) {
      if (searchParams.status && searchParams.status.length > 0) {
        searchParams.status.forEach((status) => {
          params = params.append('status', status);
        });
      }

      if (searchParams.assignedTo) {
        if (Array.isArray(searchParams.assignedTo)) {
          searchParams.assignedTo.forEach((userId) => {
            params = params.append('assignedTo', userId);
          });
        } else {
          params = params.set('assignedTo', searchParams.assignedTo);
        }
      }

      if (searchParams.createdDateFrom) {
        params = params.set('createdDateFrom', searchParams.createdDateFrom);
      }

      if (searchParams.createdDateTo) {
        params = params.set('createdDateTo', searchParams.createdDateTo);
      }

      if (searchParams.createdBy) {
        params = params.set('createdBy', searchParams.createdBy);
      }

      if (searchParams.page !== undefined) {
        params = params.set('page', searchParams.page.toString());
      }

      if (searchParams.size !== undefined) {
        params = params.set('size', searchParams.size.toString());
      }

      if (searchParams.sortBy) {
        params = params.set('sortBy', searchParams.sortBy);
      }

      if (searchParams.sortDir) {
        params = params.set('sortDir', searchParams.sortDir);
      }

      if (searchParams.searchString) {
        params = params.set('search', searchParams.searchString);
      }

      if (searchParams.amountFrom !== undefined) {
        params = params.set('amountFrom', searchParams.amountFrom.toString());
      }

      if (searchParams.amountTo !== undefined) {
        params = params.set('amountTo', searchParams.amountTo.toString());
      }

      if (searchParams.reviewedDateFrom) {
        params = params.set('reviewedDateFrom', searchParams.reviewedDateFrom);
      }

      if (searchParams.reviewedDateTo) {
        params = params.set('reviewedDateTo', searchParams.reviewedDateTo);
      }

      if (searchParams.reviewedBy) {
        params = params.set('reviewedBy', searchParams.reviewedBy);
      }

      if (searchParams.solution) {
        params = params.set('solution', searchParams.solution);
      }

      if (searchParams.transactionType) {
        params = params.set('transactionType', searchParams.transactionType);
      }

      if (searchParams.paymentReason) {
        params = params.set('paymentReason', searchParams.paymentReason);
      }
    }

    return this.apiService
      .get<ApprovalTransactionListResponse>(this.basePath, {
        params,
        context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Unable to fetch approval requests.'),
      })
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  /**
   * Fetch details of a specific approval request
   * GET /api/portal/v1/move-money/approval-requests/{approvalRequestId}
   */
  getApprovalRequestDetails(approvalRequestId: string): Observable<ApprovalTransactionDetails> {
    return this.apiService
      .get<ApprovalTransactionDetails>(`${this.basePath}/${approvalRequestId}`, {
        context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Unable to fetch approval request details.'),
      })
      .pipe(
        tap({
          next: (response) => {
            const { createdBy, updatedBy, assignedTo, requestData } = response;
            const assigneeIds: CreatedUpdatedBy[] = (assignedTo || []).map((assignee) => ({ type: 'USER', id: assignee.userId }));
            this.store.dispatch(
              AuditActions.loadTeamMembers({
                ids: compact([createdBy, updatedBy, requestData?.createdBy, ...assigneeIds]),
              }),
            );
          },
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  /**
   * Add an approver to an approval request
   * POST /api/portal/v1/move-money/approval-requests/{approvalRequestId}/approvers
   */
  addApprover(approvalRequestId: string, request: AddApproverRequest): Observable<ApprovalTransactionDetails> {
    return this.apiService
      .post<ApprovalTransactionDetails>(`${this.basePath}/${approvalRequestId}/approvers`, request, {
        context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      })
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  /**
   * Remove an approver from an approval request
   * DELETE /api/portal/v1/move-money/approval-requests/{approvalRequestId}/approvers/{userId}
   */
  removeApprover(approvalRequestId: string, userId: string): Observable<void> {
    return this.apiService
      .delete<void>(`${this.basePath}/${approvalRequestId}/approvers/${userId}`, {
        context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Unable to remove approver.'),
      })
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  /**
   * Update the status of an approval request
   * POST /api/portal/v1/move-money/approval-requests/{approvalRequestId}
   */
  updateApprovalStatus(approvalRequestId: string, request: UpdateApprovalStatusRequest): Observable<ApprovalTransactionDetails> {
    return this.apiService
      .post<ApprovalTransactionDetails>(`${this.basePath}/${approvalRequestId}`, request, {
        context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      })
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }
}
